import { apiClient } from './client'
import type { CursorPage } from './accounts'
import type { ComputationRunView } from '../types/calc'

/**
 * `GET /api/v1/program-filings/{programFilingId}/computation-runs` (`ComputationRunController`,
 * docs/api-spec.md §13, ADR-0023) — same thin, read-only convention as `api/cases.ts`. Backs the
 * Case-workspace Credit tab's "pinned final computation run" lookup: the caller filters the
 * returned page client-side for `kind === "final"` (there is no server-side `?kind=` filter on
 * this endpoint today).
 */
export async function listComputationRuns(
  programFilingId: string,
  signal?: AbortSignal,
): Promise<CursorPage<ComputationRunView>> {
  const { data } = await apiClient.get<CursorPage<ComputationRunView>>(
    `/program-filings/${programFilingId}/computation-runs`,
    { signal },
  )
  return data
}

/**
 * `POST /computation-runs/{id}/finalizations` (`ComputationRunController.finalizeRun`, ADR-0023) —
 * the one-way `finalized_at` latch. The evidence-vault story's real FR-M14.5 gate lives here: the
 * FIRST finalize of a run is checked against `evidence.api.EvidenceReadinessGate` before anything
 * is written — a critical, unsatisfied, unwaived `EvidenceRequirement` on the run's filing 409s
 * with `evidence-requirements-unsatisfied` and one `violations` entry per blocking requirement
 * (`field`=key, `message`=label), zero writes. Idempotent once finalized (a repeat call is exempt
 * from the gate and just replays the finalized view). No `Idempotency-Key`:
 * `/api/v1/computation-runs/*` is not one of `IdempotencyFilterConfig`'s covered prefixes (mirrors
 * `EvidenceVaultIntegrationTest`'s own real call).
 */
export async function finalizeComputationRun(runId: string): Promise<ComputationRunView> {
  const { data } = await apiClient.post<ComputationRunView>(`/computation-runs/${runId}/finalizations`, undefined)
  return data
}
